/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Asset,
  AssetMediaType,
  AssetOption,
  AssetOptionType,
  AssetUtils,
  Constants,
  ContentTagsUtils,
  DurationUtils,
  FileFormat,
  OptionUtils,
  ProductPrice,
  ProductPriceStatus,
  ProductPriceType,
  TimeUtils,
  WatchOption,
  WatchOptionType,
  WatchOptionVod,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { call } from 'redux-saga/effects';
import Options from 'core/store/ducks/pi/options';
import translate from 'services/i18n/translate';
import * as Context from '../context';

export enum WatchMode {
  RESTART = 'restart',
  RESUME = 'resume',
}

export enum WatchOptionProvider {
  LIVE,
  RECORDING,
  CATCHUP,
  RENTAL,
  PURCHASE,
  SUBSCRIPTION,
  COLLECTION,
  VIDEOCLUBE,
}

export interface AppWatchOption extends WatchOption {
  provider: WatchOptionProvider;
  mode?: WatchMode;
}

type ProviderKey =
  | 'buttons.watch'
  | 'buttons.watch.rental'
  | 'buttons.watch.purchase'
  | 'buttons.watch.subscription'
  | 'buttons.watch.collection'
  | 'buttons.watch.videoclub';
type ProviderHdKey =
  | 'buttons.watch.hd'
  | 'buttons.watch.rental.hd'
  | 'buttons.watch.purchase.hd'
  | 'buttons.watch.subscription.hd'
  | 'buttons.watch.collection.hd'
  | 'buttons.watch.videoclub.hd';

const RENTED_ENTITLEMENT_DURATION = DurationUtils.toDestructuredDuration(Constants.RENTED_ENTITLEMENT_DURATION);

// util
function isRental(price: ProductPrice): boolean {
  if (RENTED_ENTITLEMENT_DURATION && price.startDate && price.endDate) {
    return TimeUtils.isBefore(
      price.endDate,
      TimeUtils.addDestructuredDuration(price.startDate, RENTED_ENTITLEMENT_DURATION)
    );
  }

  return false;
}

function getStartOverWatchOption(originalWatchOption: AppWatchOption, asset: Asset): AppWatchOption | undefined {
  if (
    (AssetUtils.isEpg(asset) && ContentTagsUtils.isResartAllowedForEpgAsset(asset)) ||
    (AssetUtils.isRecording(asset) && ContentTagsUtils.isResartAllowedForRecording(asset)) ||
    (AssetUtils.isVod(asset) && ContentTagsUtils.isResartAllowedForVodAsset(asset))
  ) {
    return {
      ...originalWatchOption,
      key: `${originalWatchOption.key}-${WatchMode.RESTART}`,
      mode: WatchMode.RESTART,
      title: translate['buttons.startOver'],
      progress: 0,
    };
  }

  return undefined;
}

function getLinearWatchOption(rawOptions: WatchOption[], asset: Asset): AppWatchOption[] {
  const liveOption = rawOptions.find(
    (option) => option.watchOptionType === WatchOptionType.TV_LIVE && option.asset?.id === asset.id
  );

  if (liveOption) {
    const appLiveOption = { ...liveOption, provider: WatchOptionProvider.LIVE, title: translate['buttons.watch'] };
    const startOverOption = getStartOverWatchOption(appLiveOption, asset);
    appLiveOption.hidden = true;
    return startOverOption ? [startOverOption, appLiveOption] : [appLiveOption];
  }

  return [];
}

function getWatchOptionForLive(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption | undefined {
  const liveOptions = rawOptions.map((cur) => ({ ...cur, provider: WatchOptionProvider.LIVE }));

  if (liveOptions.length === 1) {
    return {
      ...liveOptions[0],
      title: uniqueProvider ? translate['buttons.watch'] : translate['buttons.watch.live'],
    };
  }

  // stack multiple options
  if (liveOptions.length > 1) {
    return {
      key: `${AssetOptionType.WATCH}_${WatchOptionType.TV_LIVE}`,
      type: AssetOptionType.WATCH,
      title: uniqueProvider ? translate['buttons.watch'] : translate['buttons.watch.live'],
      children: liveOptions,
      multiOptions: true,
      provider: WatchOptionProvider.LIVE,
    };
  }

  return undefined;
}

function getProgressWatchOptions(parentOption: AppWatchOption): AppWatchOption[] {
  return [
    {
      ...parentOption,
      key: `${parentOption.key}_${WatchMode.RESUME}`,
      title: translate['buttons.resume'],
      mode: WatchMode.RESUME,
    },
    {
      ...parentOption,
      key: `${parentOption.key}_${WatchMode.RESTART}`,
      title: translate['buttons.startOver'],
      mode: WatchMode.RESTART,
      progress: 0,
    },
  ];
}

function applyStackableProgress(options: AppWatchOption[]): AppWatchOption[] {
  return options.map((option) => {
    if (option.progress && option.progress > 0) {
      return {
        ...option,
        children: getProgressWatchOptions(option),
        multiOptions: true,
      };
    }
    return option;
  });
}

function translateProviderByFormat(fileFormat: FileFormat, key: ProviderKey, keyHd: ProviderHdKey): string {
  return fileFormat === FileFormat.MAIN_HD || fileFormat === FileFormat.MAIN_UHD ? translate[keyHd] : translate[key];
}

function getWatchOptionsForVodProviders(
  rawOptions: WatchOptionVod[],
  provider: WatchOptionProvider,
  key: ProviderKey,
  keyHd: ProviderHdKey
): AppWatchOption[] {
  const options = rawOptions.map((op) => {
    const title = translateProviderByFormat(op.stream.fileFormat, key, keyHd);
    return { ...op, provider, title };
  });

  return applyStackableProgress(options);
}

function getWatchOptionsForVideoClub(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  return getWatchOptionsForVodProviders(
    rawOptions as WatchOptionVod[],
    WatchOptionProvider.VIDEOCLUBE,
    uniqueProvider ? 'buttons.watch' : 'buttons.watch.videoclub',
    uniqueProvider ? 'buttons.watch.hd' : 'buttons.watch.videoclub.hd'
  );
}

function getWatchOptionsForRental(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  return getWatchOptionsForVodProviders(
    rawOptions as WatchOptionVod[],
    WatchOptionProvider.RENTAL,
    uniqueProvider ? 'buttons.watch' : 'buttons.watch.rental',
    uniqueProvider ? 'buttons.watch.hd' : 'buttons.watch.rental.hd'
  );
}

function getWatchOptionsForPurchase(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  return getWatchOptionsForVodProviders(
    rawOptions as WatchOptionVod[],
    WatchOptionProvider.PURCHASE,
    uniqueProvider ? 'buttons.watch' : 'buttons.watch.purchase',
    uniqueProvider ? 'buttons.watch.hd' : 'buttons.watch.purchase.hd'
  );
}

function getWatchOptionsForSubscription(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  return getWatchOptionsForVodProviders(
    rawOptions as WatchOptionVod[],
    WatchOptionProvider.SUBSCRIPTION,
    uniqueProvider ? 'buttons.watch' : 'buttons.watch.subscription',
    uniqueProvider ? 'buttons.watch.hd' : 'buttons.watch.subscription.hd'
  );
}

function getWatchOptionsForCollection(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  return getWatchOptionsForVodProviders(
    rawOptions as WatchOptionVod[],
    WatchOptionProvider.COLLECTION,
    uniqueProvider ? 'buttons.watch' : 'buttons.watch.collection',
    uniqueProvider ? 'buttons.watch.hd' : 'buttons.watch.collection.hd'
  );
}

function getWatchOptionsForRecording(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  const options = rawOptions.map((op) => {
    let title = '';
    if (uniqueProvider && rawOptions.length === 1) {
      title = translate['buttons.watch'];
    } else if (uniqueProvider && rawOptions.length > 1) {
      title = translate.formatString(translate['buttons.watch.channel'], op.channel?.title || '').toString();
    } else if (!uniqueProvider && rawOptions.length === 1) {
      title = translate['buttons.watch.recording'];
    } else if (!uniqueProvider && rawOptions.length > 1) {
      title = translate.formatString(translate['buttons.watch.recording.channel'], op.channel?.title || '').toString();
    }
    return {
      ...op,
      provider: WatchOptionProvider.RECORDING,
      title,
    };
  });

  return applyStackableProgress(options);
}

function getWatchOptionsForCatchup(rawOptions: WatchOption[], uniqueProvider: boolean): AppWatchOption[] {
  const options = rawOptions.map((op) => {
    let title = '';
    if (uniqueProvider && rawOptions.length === 1) {
      title = translate['buttons.watch'];
    } else if (uniqueProvider && rawOptions.length > 1) {
      title = translate.formatString(translate['buttons.watch.channel'], op.channel?.title || '').toString();
    } else if (!uniqueProvider && rawOptions.length === 1) {
      title = translate['buttons.watch.catchup'];
    } else if (!uniqueProvider && rawOptions.length > 1) {
      title = translate.formatString(translate['buttons.watch.catchup.channel'], op.channel?.title || '').toString();
    }
    return {
      ...op,
      provider: WatchOptionProvider.CATCHUP,
      title,
    };
  });

  return applyStackableProgress(options);
}

function sliptRawWatchOptionsByProvider(rawOptions: WatchOption[]): Map<WatchOptionProvider, WatchOption[]> {
  const rawOptionsByProvider = new Map<WatchOptionProvider, WatchOption[]>();

  let provider: WatchOptionProvider;
  rawOptions.forEach((cur) => {
    if (cur.watchOptionType === WatchOptionType.TV_LIVE) {
      provider = WatchOptionProvider.LIVE;
    } else if (OptionUtils.isWatchVod(cur) && cur.price?.type === ProductPriceType.TVOD && isRental(cur.price)) {
      provider = WatchOptionProvider.RENTAL;
    } else if (OptionUtils.isWatchVod(cur) && cur.price?.type === ProductPriceType.TVOD && !isRental(cur.price)) {
      provider = WatchOptionProvider.PURCHASE;
    } else if (OptionUtils.isWatchVod(cur) && cur.price?.type === ProductPriceType.BOXSET) {
      provider = WatchOptionProvider.COLLECTION;
    } else if (OptionUtils.isWatchVod(cur) && cur.price?.type === ProductPriceType.SVOD) {
      provider = WatchOptionProvider.SUBSCRIPTION;
    } else if (cur.watchOptionType === WatchOptionType.TV_PAST && cur.asset && AssetUtils.isRecording(cur.asset)) {
      provider = WatchOptionProvider.RECORDING;
    } else if (cur.watchOptionType === WatchOptionType.TV_PAST && cur.asset && AssetUtils.isEpg(cur.asset)) {
      provider = WatchOptionProvider.CATCHUP;
    } else if (OptionUtils.isWatchVod(cur) && cur.price?.status === ProductPriceStatus.FREE) {
      provider = WatchOptionProvider.VIDEOCLUBE;
    }

    if (!rawOptionsByProvider.get(provider)) {
      rawOptionsByProvider.set(provider, []);
    }

    rawOptionsByProvider.get(provider)?.push(cur);
  });

  return rawOptionsByProvider;
}

function getWatchOptionsByProvider(rawOptions: WatchOption[]): AppWatchOption[] {
  const rawOptionsByProvider = sliptRawWatchOptionsByProvider(rawOptions);

  // this is used in the labels logic
  // because with a unique provider available the labels don't need to include the provider reference
  // Example: 'Watch Live' will be just 'Watch'
  const uniqueProvider = rawOptionsByProvider.size === 1;

  const allOptions = [
    ...getWatchOptionsForRental(rawOptionsByProvider.get(WatchOptionProvider.RENTAL) || [], uniqueProvider),
    ...getWatchOptionsForPurchase(rawOptionsByProvider.get(WatchOptionProvider.PURCHASE) || [], uniqueProvider),
    ...getWatchOptionsForCollection(rawOptionsByProvider.get(WatchOptionProvider.COLLECTION) || [], uniqueProvider),
    ...getWatchOptionsForSubscription(rawOptionsByProvider.get(WatchOptionProvider.SUBSCRIPTION) || [], uniqueProvider),
    ...getWatchOptionsForRecording(rawOptionsByProvider.get(WatchOptionProvider.RECORDING) || [], uniqueProvider),
    ...getWatchOptionsForCatchup(rawOptionsByProvider.get(WatchOptionProvider.CATCHUP) || [], uniqueProvider),
    ...getWatchOptionsForVideoClub(rawOptionsByProvider.get(WatchOptionProvider.VIDEOCLUBE) || [], uniqueProvider),
  ];

  const liveOption = getWatchOptionForLive(rawOptionsByProvider.get(WatchOptionProvider.LIVE) || [], uniqueProvider);

  if (liveOption) {
    allOptions.unshift(liveOption);
  }

  rawOptionsByProvider.clear();

  return allOptions;
}

export default function* getWatchOptions(
  entryAssetMediaType: string,
  asset: Asset,
  clones: Asset[],
  vodClonesPrices: ProductPrice[]
): SagaIterator<AssetOption[]> {
  try {
    // get all options
    const watchOptions: WatchOption[] = yield call(Options.getWatchOptions, asset, clones, vodClonesPrices);

    // adapt options for the App UI
    if (entryAssetMediaType === AssetMediaType.LINEAR) {
      return getLinearWatchOption(watchOptions, asset);
    }

    return getWatchOptionsByProvider(watchOptions);
  } catch {
    return [];
  }
}
